import he from 'he';
import fp from 'lodash/fp';
import { pageLayouts, pageSizes } from '../constants';
import { noCapMap, unfixedFloor } from '../utils';
import { percentageChart, reportHeader, table } from './partials';
import { progressGoalCharts, shiftAndUnshift } from './partials/progress-goal-charts';

const chartOffset = { x: 110, y: 120 };
const tableOffset = { x: -110, y: 60 };

const riskColours = {
  low: '#1FD1A1',
  medium: '#FFC42B',
  high: '#FD3C64'
};

const mapRiskRateToColumn = (riskRate) => ({
  text: {
    colour: '#fff',
    value: riskRate
  },
  colour: riskRate === 'Low' ?
    riskColours.low :
    riskRate === 'Medium' ?
      riskColours.medium :
      riskColours.high
})

const totalTeamCpdProgressReport = ({
  pageLayout,
  pageSize,
  reportee,
  afsl,
  minDate,
  maxDate,
  businessTeamsWithStats: {
    allMembersStats
  },
  totalTeamProgressRows,
  TeamsReportStats,
  defaultProgressLabel
}) => [
    ...reportHeader({ report: 'Team Activity Report', reportee, afsl, minDate, maxDate }),
    ['translate', fp.values(chartOffset)],
    ['fontSize', [24]],
    ['font', ['Helvetica']],
    ['fillColour', ['#666666']],
    // ['text', [`${reportee} Team Summary`, 200, -8]],
    ['translate', fp.values({ x: 0, y: 50 })],
    ...percentageChart(unfixedFloor(allMembersStats.totalProgressPercentage, 1)),
    ['fontSize', [12]],
    ['font', ['Helvetica']],
    ['fillColour', ['#666666']],
    ['text', [`${defaultProgressLabel}`, -60, -60, {
      width: 120,
      align: 'center'
    }]],
    ...shiftAndUnshift(progressGoalCharts(allMembersStats.goals)),
    ['translate', fp.values(tableOffset)],
    ...table({
      pageLayout,
      pageSize: pageSize || 'A4',
      remainingPageHeight: fp.get(
        `${pageSize || 'A4'}.${pageLayout === pageLayouts.landscape ? 'x' : 'y'}`
      )(pageSizes) - (chartOffset.y + tableOffset.y + 50),
      reportHeader: [...reportHeader({ report: 'Activity Report', reportee, afsl, minDate, maxDate }), ['translate', [0, tableOffset.y]],],
      headerRow: {
        colour: '#1fd1a1',
        rowLines: 2,
        text: {
          colour: '#fff'
        },
        columns: [
          {
            colSpan: 2,
            text: 'Team'
          },
          { text: 'Team size' },
          { text: 'Training hours met' },
          { text: 'Training plan hours' },
          { text: 'Total progress hours' },
          { text: 'Shortfall hours' },
          { text: 'Total progress %' },
          { text: 'Shortfall %' },
          { text: 'Days left' },
          { text: 'Risk rating' }
        ]
      },
      rows:
        noCapMap(({
          name,
          teamSize,
          trainingHoursMet,
          totalCpdPoints,
          totalProgressPercentage,
          totalProgressHours,
          shortFall,
          shortFallHours,
          tasaCpdHours,
          daysLeft,
          riskRating,
          className
        }, index) => ({
          colour: index % 2 === 0 ? '#fff' : '#EFEFEF',
          bold: (className === 'heavy') ? true : false,
          columns:
            [
              {
                colSpan: 2,
                text: name
              },
              { text: teamSize },
              { text: trainingHoursMet },
              { text: totalCpdPoints },
              { text: totalProgressHours },
              { text: shortFallHours },
              { text: totalProgressPercentage },
              { text: shortFall },
              { text: daysLeft },
              mapRiskRateToColumn(riskRating)
            ]
        })
        )(totalTeamProgressRows)
    }),
    ...fp.flatten(noCapMap((teamWithStats, index) => (
      teamCpdProgressReport({
        pageLayout,
        pageSize,
        reportee,
        afsl,
        minDate,
        maxDate,
        teamWithStats,
        defaultProgressLabel
      })))(TeamsReportStats)
    )
  ];

const teamCpdProgressReport = ({
  pageLayout,
  pageSize,
  afsl,
  minDate,
  maxDate,
  teamWithStats: {
    teamProgress,
    goals,
    teamName,
    membersStatsRows
  },
  defaultProgressLabel
}) => [
    ['addPage'],
    ...reportHeader({ report: 'Team Activity Report', reportee: he.decode(teamName), afsl, minDate, maxDate }),
    ['translate', fp.values(chartOffset)],
    ['fontSize', [24]],
    ['font', ['Helvetica']],
    ['fillColour', ['#666666']],
    // ['text', [`${he.decode(teamName)} Total CPD Progress`, 200, -8]],
    ['translate', fp.values({ x: 0, y: 50 })],
    ...percentageChart(unfixedFloor(teamProgress, 1)),
    ['fontSize', [12]],
    ['font', ['Helvetica']],
    ['fillColour', ['#666666']],
    ['text', [`${defaultProgressLabel}`, -60, -60, {
      width: 120,
      align: 'center'
    }]],
    ...shiftAndUnshift(progressGoalCharts(goals)),
    ['translate', fp.values(tableOffset)],
    ...table({
      pageLayout,
      pageSize: pageSize || 'A4',
      remainingPageHeight: fp.get(
        `${pageSize || 'A4'}.${pageLayout === pageLayouts.landscape ? 'x' : 'y'}`
      )(pageSizes) - (chartOffset.y + tableOffset.y + 50),
      reportHeader: [...reportHeader({ report: 'Activity Report', reportee: he.decode(teamName), afsl, minDate, maxDate }), ['translate', [0, tableOffset.y]],],
      headerRow: {
        colour: '#1fd1a1',
        rowLines: 2,
        text: {
          colour: '#fff'
        },
        columns: [
          {
            colSpan: 2,
            text: 'Team'
          },
          { text: 'Training hours met' },
          { text: 'Training plan hours' },
          { text: 'Total progress hours' },
          { text: 'Shortfall hours' },
          { text: 'Total progress %' },
          { text: 'Shortfall %' },
          { text: 'Days left' },
          { text: 'Risk rating' }
        ]
      },
      rows:
        noCapMap(({
          name,
          trainingHoursMet,
          totalCpdPoints,
          totalProgressPercentage,
          totalProgressHours,
          shortFall,
          shortFallHours,
          tasaCpdHours,
          daysLeft,
          riskRating,
          className
        }, index) => ({
          colour: index % 2 === 0 ? '#fff' : '#EFEFEF',
          bold: className === 'heavy' ? true : false,
          columns: [
            {
              colSpan: 2,
              text: name
            },
            { text: trainingHoursMet },
            { text: totalCpdPoints },
            { text: totalProgressHours },
            { text: shortFallHours },
            { text: totalProgressPercentage },
            { text: shortFall },
            { text: daysLeft },
            mapRiskRateToColumn(riskRating)
          ]
        })
        )(membersStatsRows)
    })
  ];

export default totalTeamCpdProgressReport;



// WEBPACK FOOTER //
// ./src/reports/total-team-cpd-progress.js